import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { devLog } from "@/lib/log";

/**
 * ErrorBoundary — surrounds a page (or a sub-tree) so a runtime crash in one
 * child (e.g. a rogue supplier chip, a bad AppContext snapshot, a script-error
 * swallowed by CORS) doesn't wipe the whole screen for a tradie who's just
 * trying to snap a docket. Renders a small friendly fallback with a
 * "Reload" button and logs the offending stack so devs still see the trace.
 *
 * Class component intentionally — React error boundaries are still only
 * available via componentDidCatch / getDerivedStateFromError. Kept
 * deliberately small; anything richer belongs in Sentry.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    // `resetKey` is bumped on Reload — the wrapping caller uses it to
    // remount its child sub-tree via `key={resetKey}` so any cached bad
    // state in a context / hook is torn down instead of instantly
    // re-throwing on the next render.
    this.state = { hasError: false, msg: "", resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    // Coerce non-Error throws (some libs throw strings) to a stable string.
    const raw = (error && (error.message || error.toString())) || "Unknown error";
    return { hasError: true, msg: String(raw).slice(0, 240) };
  }

  componentDidCatch(error, info) {
    devLog.warn("ErrorBoundary caught", error, info?.componentStack);
  }

  handleReload = () => {
    // Bump the reset key so the caller-provided render function can
    // remount its children (see Layout's <Outlet /> wrapping). Soft-reset
    // alone would keep the same cached hook/context values and often
    // re-throw immediately.
    this.setState((s) => ({ hasError: false, msg: "", resetKey: s.resetKey + 1 }));
    if (typeof this.props.onReload === "function") {
      try { this.props.onReload(); } catch { /* ignore */ }
    }
  };

  render() {
    if (!this.state.hasError) {
      // Support two consumer patterns:
      //   (a) Plain children — most common; boundary is transparent.
      //   (b) A render-prop `children(resetKey)` so the caller can attach
      //       the key to its own tree (used by Layout for <Outlet />).
      return typeof this.props.children === "function"
        ? this.props.children(this.state.resetKey)
        : this.props.children;
    }
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-[60vh] flex items-center justify-center px-4"
      >
        <div className="bi-surface p-6 max-w-md w-full space-y-4 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#F87171]/15 text-[#F87171] flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="font-heading font-extrabold text-white text-lg">
              Something went sideways
            </h2>
            <p className="text-sm text-white/60 mt-1 leading-relaxed">
              This screen crashed mid-render. Nothing you saved is lost — tap
              reload and it should come right back.
            </p>
          </div>
          {this.state.msg && (
            <pre className="text-[11px] text-left text-white/40 bg-black/30 rounded-lg p-2 overflow-x-auto">
              {this.state.msg}
            </pre>
          )}
          <button
            data-testid="error-boundary-reload"
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bi-accent-btn text-sm font-heading font-bold uppercase tracking-wider"
          >
            <RefreshCw className="w-4 h-4" /> Reload
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="block mx-auto text-[11px] text-white/40 hover:text-white/70 underline"
          >
            Full page refresh
          </button>
        </div>
      </div>
    );
  }
}
